import React, { useState } from 'react'
import Label from '../components/ui/Label';
import Input from '../components/ui/Input';
import { useDispatch, useSelector } from 'react-redux';
import { clearLoginCredential, selectLoginStaffEmail, selectLoginStaffPassword, setCredentials, setLoginCredential } from '../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../toastify/Toast';
import CBLogoLogIn from '../assets/images/capital brew logo.png'
import Loading from '../components/ui/Loading';
import { handleApiError } from '../utils/handleApiError';
import { BasilEyeClosedOutline, IconamoonEyeLight } from '../assets/icons/Icons';
import { UserCredential } from '../types/response';
import { getNavigatePage } from '../hooks/useNavigatePage';
import { useMutationData } from '../hooks/api/useMutationData';

export default function LoginStaff() {

    const dispatch = useDispatch()
    const navigate = useNavigate();
    const { loginStaff, isLoadingLoginStaff } = useMutationData()
    const email = useSelector(selectLoginStaffEmail)
    const password = useSelector(selectLoginStaffPassword)
    const [showPassword, setShowPassword] = useState(false);
    const isDisableBtn = !email.trim() || !password.trim() || password.length !== 6;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if(!email || !password) {
            showToast("Email and password is required", 'error');
        }
        try {
            const response = await loginStaff({ email, password }).unwrap();
            const staffData: UserCredential = {
                isNewUsername: false,
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
                staff: {
                    staffID: response.staff.staffID,
                    firstName: response.staff.firstName,
                    middleInitial: response.staff.middleInitial,
                    lastName: response.staff.lastName,
                    email: response.staff.email,
                    phone: null,
                    status: '',
                    role: response.staff.role,
                    roleId: null,
                    statusId: null,

                    sessionId: null,
                    clientID: null,
                    userName: '',
                    generatedUsername: '',
                    deceasedName: '',
                    token: '',
                }
            };
            
            dispatch(setCredentials(staffData));
            navigate(getNavigatePage(response.staff.role?.toLowerCase()), {replace: true});
            dispatch(clearLoginCredential({type: 'staff'}))
        } catch (error: any) {
            if(!error.response){
                handleApiError(error, "Server is not reachable. Please try again later.");
            } else {
                handleApiError(error, "Login failed. Please try again.");
            }
        }
    }

    return (
        <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-orange-50 px-4'>
            <div className='container flex flex-col items-center justify-center'>
                <div className='max-w-md w-full'>
                    <div className='text-center'>
                        <img 
                            src={CBLogoLogIn}
                            alt="Capital Brew Logo" 
                            className="mx-auto w-[300px] h-[120px] object-contain md:w-[380px] mdLh-[200px]"
                        />
                        <p className='text-gray-600 pb-3'>Fresh coffee and warm bread, just a tap away.</p>
                    </div>
                    <div className='bg-white p-8 rounded-2xl shadow-lg'>
                        <form 
                            className='space-y-4'
                        >
                            <div>
                                <Label label={'Email *'}/>
                                <Input
                                    type={'email'}
                                    value={email}
                                    padding='py-2 px-3'
                                    textSize='text-xs'
                                    onChange={(e) => dispatch(setLoginCredential({ stateType: 'staff', type: 'email', value: e.target.value }))}
                                    placeholder={'example@gmail.com'}
                                    required
                                />
                            </div>
                            <div className=''>
                                <Label label={'Password *'}/>
                                <div className='relative'>
                                    <input 
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => dispatch(setLoginCredential({ stateType: 'staff', type: 'password', value: e.target.value }))}
                                        className={`block w-full text-xs rounded border border-gray-300 px-3 py-2 pr-10 shadow-sm outline-none placeholder:text-gray-400 focus:border-black`}
                                        placeholder={'*******'}
                                        required
                                        maxLength={6}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-2 flex items-center justify-center text-gray-500"
                                    >
                                        {password.length !== 0 && (
                                            showPassword ? (
                                                <IconamoonEyeLight width={18} height={18} />
                                            ) : (
                                                <BasilEyeClosedOutline width={18} height={18} />
                                            )
                                        )}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isLoadingLoginStaff || isDisableBtn}
                                className={`${isLoadingLoginStaff || isDisableBtn ? 'bg-gray-200 text-black cursor-not-allowed' : 'bg-cbColor text-white'} inline-flex w-full items-center justify-center rounded py-2 text-sm font-medium`}
                            >
                                {isLoadingLoginStaff ? (
                                    <Loading setH={'h-[20px]'} message={'Signing In...'}/>
                                ) : ('Sign In')}
                            </button>
                        </form>
                    </div>
                </div>
                <p className='text-center text-sm pt-[30px]'>Copyright © {new Date().getFullYear()} PhilLife. All Rights Reserved</p>
            </div>
        </div>
    )
}
